import { useModal } from '@ebay/nice-modal-react';
import { useUserEventLogger } from '@repo/tracking-service';
import { useDialog } from '@repo/ui';
import { Button, Fieldset, TextField, useToast } from '@zenterprise-inc/ui';
import { useRouter } from 'next/router';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useToggle } from 'react-use';

import { BankCode } from '@/__generated__/checkBankAccountMutation.graphql';
import { TitleByPhoneNumber } from '@/components/tax-refund/bank-account/TitleByPhoneNumber';
import { useBankAccountContext } from '@/components/tax-refund/bank-account/contexts/bank-account-context';
import { usePhoneNumberInBznavUserInfo } from '@/components/tax-refund/bank-account/contexts/phone-number-context';
import BankListDrawer from '@/components/tax-refund/common/drawers/BankListDrawer';
import { CheckAccountNameDrawer } from '@/components/tax-refund/common/drawers/CheckAccountNameDrawer';
import { useBothApplyRefunds } from '@/components/tax-refund/common/hooks/use-both-apply-refund';
import checkBankMaintenanceTime from '@/components/tax-refund/common/utils/checkBankMaintenanceTime';
import { getRefundDataForWebStorage, setRefundDataForWebStorage, SetRefundDataForWebStorageParams } from '@/components/tax-refund/common/utils/refund-data';
import { INFOTECH_BANK } from '@/lib/constants/bank';
import { COMMON_PATHS } from '@/lib/constants/paths';
import { useCheckBankAccount } from '@/lib/hooks/api/mutation/use-check-bank-account';
import { ellipsisName } from '@/lib/utils/ellipsisName';

export const RefundBankAccountContent = () => {
  const bankListDrawer = useModal(BankListDrawer);
  const { refundAmount, refundAmountTotal, refundId, bizName, businessType } = useBankAccountContext();
  const { phoneNumber, hasBznavPhoneNumber } = usePhoneNumberInBznavUserInfo();
  const { bothApplyRefunds } = useBothApplyRefunds();
  const [inputAccount, setInputAccount] = useState({
    bankId: '',
    accountNumber: ''
  });

  const { pageName, sendClickEvent } = useUserEventLogger();
  const router = useRouter();
  const toast = useToast();
  const { showDialog } = useDialog();

  const [showCheckAccountDrawer, toggleCheckAccountDrawer] = useToggle(false);

  const [error, setError] = useState(false);
  const [disabled, setDisabled] = useState(true);

  const [verifiedBankAccountName, setVerifiedBankAccountName] = useState('');
  const { commit: checkBankAccount, isInFlight } = useCheckBankAccount();

  const bankName = useMemo(() => {
    return INFOTECH_BANK.find(({ bankId }) => bankId === inputAccount.bankId)?.bankName || '';
  }, [inputAccount]);

  const handleAccountNumberChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target as HTMLInputElement;
    const newValue = value.replace(/[^0-9.]/g, '');
    setInputAccount((prev) => ({
      ...prev,
      accountNumber: newValue
    }));

    setError(newValue.length === 0);
    setDisabled(!/^\d{10,14}$/.test(newValue));
  }, []);

  const handleClickBank = (bankId: string) => {
    setInputAccount((prev) => ({
      ...prev,
      bankId
    }));
    bankListDrawer.hide();
  };

  const handleClickNext = () => {
    sendClickEvent(`${pageName}_bankaccount_next`);
    checkBankAccount({
      variables: {
        bankCode: inputAccount.bankId as BankCode,
        account: inputAccount.accountNumber
      },
      onCompleted: (response, errors) => {
        if (errors && errors?.length > 0) {
          toast({
            content: errors[0]?.message ?? '',
            icon: 'error',
            iconColor: 'red.600'
          });
          return;
        }

        const result = response?.checkBankAccount?.result;
        const name = response?.checkBankAccount?.name ?? '';

        if (result) {
          setVerifiedBankAccountName(name);
          toggleCheckAccountDrawer();
        } else {
          const isBankMaintenanceTime = checkBankMaintenanceTime();
          showDialog({
            title: '계좌를 다시 입력해 주세요',
            description: `존재하지 않는 계좌번호예요. 계좌번호를 다시 확인해주세요.${isBankMaintenanceTime ? '\n\n*23:50~24:30 사이에는 은행망 점검으로 인하여 계좌 조회 오류가 발생할 수 있어요.' : ''}`
          });
        }
      },
      onError: () => {
        toast({
          content: '계좌 실명 확인 중 에러 발생',
          icon: 'error',
          iconColor: 'red.600'
        });
      }
    });
  };

  const handleClickYes = () => {
    toggleCheckAccountDrawer();

    const sessionStorageRefundData = getRefundDataForWebStorage(businessType);
    if (!sessionStorageRefundData[businessType].refundId && !sessionStorageRefundData[businessType]?.tritxRefundInfo?.refundId) {
      toast({ content: '신청 정보가 없습니다.' });
      return;
    }

    const bankAccountInputData = {
      bank: bankName,
      accountNo: inputAccount.accountNumber,
      accountOwner: verifiedBankAccountName
    };

    setRefundDataForWebStorage({
      type: businessType,
      data: {
        ...sessionStorageRefundData[businessType],
        ...bankAccountInputData
      }
    } as SetRefundDataForWebStorageParams);

    // 유저 정보에 휴대폰 번호가 있는 경우 신청 완료 처리
    if (hasBznavPhoneNumber) {
      bothApplyRefunds({
        variables: {
          refundId,
          phoneNumber,
          ...bankAccountInputData
        },
        refundAmountTotal,
        refundAmount: refundAmount ?? 0,
        businessType
      });
      return;
    }
    // 유저 정보에 휴대폰 번호가 없는 경우 휴대폰 번호 입력 페이지로 이동
    router.push({
      pathname: `${COMMON_PATHS.환급본인휴대폰번호입력}`,
      query: router.query
    });
  };

  return (
    <div className="page-padding flex w-full flex-col pb-24">
      <TitleByPhoneNumber bizName={bizName} businessType={businessType} hasBznavPhoneNumber={hasBznavPhoneNumber} />
      <Fieldset.Wrapper>
        <Fieldset.Label>은행</Fieldset.Label>
        <TextField.Select
          value={bankName}
          placeholder="은행 선택"
          onClick={() => {
            sendClickEvent(`${pageName}_bankaccount_bank`);
            bankListDrawer.show({ keepMounted: true, onClick: handleClickBank });
          }}
        />
      </Fieldset.Wrapper>
      <Fieldset.Wrapper isInvalid={error}>
        <Fieldset.Label>계좌번호</Fieldset.Label>
        <TextField.Basic
          type="tel"
          inputMode="numeric"
          onChange={handleAccountNumberChange}
          placeholder="본인 명의 계좌만 가능"
          value={inputAccount.accountNumber}
          className="w-full"
          onClick={() => sendClickEvent(`${pageName}_bankaccount_input-account`)}
        />

        <Fieldset.Error>계좌번호를 입력해주세요.</Fieldset.Error>
      </Fieldset.Wrapper>

      <Button.StickyBottom.Wrapper className="page-padding">
        <Button.StickyBottom.Button loading={isInFlight} disabled={disabled} state="primary" onClick={handleClickNext}>
          다음
        </Button.StickyBottom.Button>
      </Button.StickyBottom.Wrapper>
      <CheckAccountNameDrawer
        isVisible={showCheckAccountDrawer}
        onCloseDrawer={toggleCheckAccountDrawer}
        onClickYes={handleClickYes}
        accountName={verifiedBankAccountName}
        account={inputAccount}
        title={`${businessType === 'corps' ? `'${ellipsisName(bizName, '고객')}'의` : `${ellipsisName(bizName, '고객')}님의`} 계좌가 맞나요?`}
      />
    </div>
  );
};
